import { useSession } from '@batiflow/client-core/react';
import { DateText, isolate } from '@batiflow/ui';
import { Button, __css, tokens } from '@fluentui/react-components';
import { Dismiss20Regular, DocumentCheckmark20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalStatusBadge } from './approval-badges';
import { ApprovalStepper } from './approval-stepper';
import { CancelFlowDialog } from './cancel-flow-dialog';
import { DecisionDialog } from './decision-dialog';
import { canCancel, canDecide, documentLabel, type ApprovalFlow } from './approvals-model';

const useStyles = __css({"card":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"meta":{"sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"}});

/** "Give my opinion" and "Cancel the approval", with their dialogs, for the actions the caller has. */
export function FlowActions({ flow }: { flow: ApprovalFlow }) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const [dialog, setDialog] = useState<'decide' | 'cancel' | null>(null);
  const label = isolate(documentLabel(flow.documentCode, flow.documentTitle));
  const decide = canDecide(flow);
  const cancel = canCancel(flow) && flow.status === 'in_progress';
  if (!decide && !cancel) return null;
  const close = () => {
    setDialog(null);
  };
  return (
    <>
      <div className={styles.actions}>
        {decide ? (
          <Button
            size="small"
            appearance="primary"
            icon={<DocumentCheckmark20Regular />}
            aria-label={t('approvals.flow.decideFor', { document: label })}
            onClick={() => {
              setDialog('decide');
            }}
          >
            {t('approvals.flow.decide')}
          </Button>
        ) : null}
        {cancel ? (
          <Button
            size="small"
            icon={<Dismiss20Regular />}
            aria-label={t('approvals.flow.cancelFor', { document: label })}
            onClick={() => {
              setDialog('cancel');
            }}
          >
            {t('approvals.flow.cancel')}
          </Button>
        ) : null}
      </div>
      {dialog === 'decide' ? <DecisionDialog flow={flow} onClose={close} /> : null}
      {dialog === 'cancel' ? <CancelFlowDialog flow={flow} onClose={close} /> : null}
    </>
  );
}

export interface FlowCardProps {
  flow: ApprovalFlow;
}

/** One flow: its state, who asked and when, the stepper and the actions the caller has. */
export function FlowCard({ flow }: FlowCardProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const userId = useSession((s) => s.me?.user.id);
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <ApprovalStatusBadge status={flow.status} />
        {flow.revisionIndex ? (
          <span className={styles.meta}>
            {t('approvals.flow.index')} <bdi dir="ltr">{flow.revisionIndex}</bdi>
          </span>
        ) : null}
        <span className={styles.meta}>
          {flow.startedByName
            ? t('approvals.flow.startedBy', { name: isolate(flow.startedByName) })
            : t('approvals.flow.started')}{' '}
          <DateText value={flow.createdAt} variant="dateTime" />
        </span>
        {flow.completedAt ? (
          <span className={styles.meta}>
            {t('approvals.flow.completed')} <DateText value={flow.completedAt} variant="dateTime" />
          </span>
        ) : flow.dueOn ? (
          <span className={styles.meta}>
            {t('approvals.flow.due')} <DateText value={flow.dueOn} />
          </span>
        ) : null}
      </div>
      <ApprovalStepper flow={flow} currentUserId={userId} />
      <FlowActions flow={flow} />
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/flow-card.griffel.css";